import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AlertCircle, ArrowLeft, Info } from "lucide-react";
import { useEvangelismAssistant } from "./EvangelismAssistantProvider";
import { useToast } from "@/hooks/use-toast";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
import { sendMessage } from "./services/evangelismService";
import ConversionComplete from "./ConversionComplete";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import SuggestionsOverlay from "./SuggestionsOverlay";

interface ChatInterfaceProps {
  worldview: string;
  difficulty: string;
  onReset: () => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ worldview, difficulty, onReset }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showConversionComplete, setShowConversionComplete] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [isGettingHelp, setIsGettingHelp] = useState(false);
  const [helpSuggestions, setHelpSuggestions] = useState<string[]>([]);
  const [showSuggestionsOverlay, setShowSuggestionsOverlay] = useState(false);
  const { messages, addMessage, resetChat } = useEvangelismAssistant();
  const { toast } = useToast();

  useEffect(() => {
    // Start the conversation with an AI message
    if (messages.length === 0) {
      const initialMessages: Record<string, string> = {
        atheist: "I don't see why anyone would believe in a god. There's just no evidence for it.",
        muslim: "I respect Jesus as a prophet, but Muhammad received the final revelation from Allah.",
        jew: "I believe in God, but I don't understand why Christians think Jesus was the Messiah.",
        hindu: "In my tradition, we believe there are many paths to God, not just one.",
        mormon: "I believe Jesus is the Son of God, but I also believe in the Book of Mormon.",
        jehovah: "Jesus is not God. He was the first creation of Jehovah.",
        blackhebrew: "The real Israelites are Black. The Bible was written about us, not white people.",
        newage: "I'm spiritual, but I don't believe in organized religion. I find my own truth.",
        universalist: "I believe everyone will be saved in the end. God is too loving to condemn anyone.",
        pluralist: "All religions are valid paths to the same divine reality. Christianity is just one way."
      };

      addMessage("ai", initialMessages[worldview] || "Hello, I'd like to talk about religion and spirituality. What do you believe?");
    }
  }, [worldview, messages.length, addMessage]);

  // Function to analyze user message for encouragement (less frequent)
  const analyzeUserMessage = async (message: string) => {
    // Only analyze every 3rd message to reduce frequency
    if (messages.length % 3 !== 0) return;
    
    try {
      const { data, error } = await supabase.functions.invoke('user-encouragement', {
        body: { message, worldview }
      });

      if (error) {
        console.error('Error analyzing user message:', error);
        return;
      }

      if (data?.encourage) {
        const techniqueEmojis = {
          scripture: "📖",
          logic: "🎯", 
          love: "❤️",
          worldview: "💪",
          testimony: "✨",
          apologetics: "🔍",
          gospel: "🙏"
        };
        
        const emoji = techniqueEmojis[data.technique as keyof typeof techniqueEmojis] || "👏";
        
        toast({
          title: `${emoji} Good!`,
          description: data.message,
          variant: "default",
          duration: 2000, // Disappears faster
        });
      }
    } catch (error) {
      console.error('Error analyzing user message:', error);
    }
  };

  const handleSendMessage = async (message: string) => {
    if (!message.trim()) return;
    
    addMessage("user", message);
    setIsLoading(true);
    setConnectionError(null);
    
    // Analyze user message for encouragement (don't wait for it)
    analyzeUserMessage(message);
    
    try {
      const result = await sendMessage(worldview, message, messages, difficulty);
      
      // Add AI response to chat
      addMessage("ai", result.response);
      
      // Check for conversion and show celebration
      if (result.converted) {
        setTimeout(() => {
          toast({
            title: "Praise God! 🎉",
            description: "Continue to disciple them.",
            variant: "default",
            duration: 3000,
          });
          setShowConversionComplete(true);
        }, 1000);
      }
    } catch (error) {
      console.error("Error fetching response:", error);
      
      // Set connection error message
      if (error instanceof Error) {
        setConnectionError(error.message);
      } else {
        setConnectionError("Something went wrong. Please try again.");
      }
      
      // Add a fallback message to the chat
      addMessage("ai", "I'm sorry, I'm having trouble responding right now. Can we try again?");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    resetChat();
    onReset();
  };

  const handleRetry = () => {
    setConnectionError(null);
    // Force a scroll refresh to make UI more responsive
    window.dispatchEvent(new Event('resize'));
  };

  const handleGetHelp = async () => {
    if (messages.length < 2) {
      toast({
        title: "Need more conversation",
        description: "Have a bit more conversation first, then I can provide better guidance.",
        variant: "default",
      });
      return;
    }

    setIsGettingHelp(true);
    setHelpSuggestions([]);

    try {
      const { data, error } = await supabase.functions.invoke('evangelism-help', {
        body: {
          worldview,
          conversation_history: messages,
          current_stage: "natural",
          difficulty
        },
      });

      if (error) {
        console.error('Error getting help:', error);
        throw new Error(error.message || 'Failed to get help suggestion');
      }

      setHelpSuggestions(data.suggestions || []);
      setShowSuggestionsOverlay(true);
      toast({
        title: "Suggestions Ready 🎯",
        description: "Choose your response approach.",
        variant: "default",
        duration: 1000,
      });
    } catch (error) {
      console.error('Error fetching help:', error);
      toast({
        title: "Error getting help",
        description: "Unable to get suggestions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGettingHelp(false);
    }
  };

  const handleSelectSuggestion = (suggestion: string) => {
    handleSendMessage(suggestion);
    setShowSuggestionsOverlay(false);
    setHelpSuggestions([]);
  };

  return (
    <div className="flex flex-col space-y-4">
      <Button 
        variant="outline" 
        className="self-start mb-2 border-heaven-gold/50 text-heaven-gold hover:bg-heaven-gold/10"
        onClick={handleReset}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Choose a different worldview
      </Button>
      
      {connectionError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Connection Error</AlertTitle>
          <AlertDescription>
            {connectionError}
            <Button 
              variant="outline" 
              size="sm" 
              className="ml-2 mt-2"
              onClick={handleRetry}
            >
              Try Again
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      <div className="w-full">
        <Card className="border-heaven-gold/20 flex flex-col h-[550px] w-full">
          <div className="flex-1 p-4 overflow-hidden">
            <MessageList isLoading={isLoading} />
          </div>
          <div className="border-t border-gray-200 w-full">
            <MessageInput 
              onSendMessage={handleSendMessage} 
              isLoading={isLoading} 
            />
            <div className="px-4 pb-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleGetHelp}
                disabled={isGettingHelp || isLoading}
                className="w-full border-heaven-gold/30 text-heaven-gold hover:bg-heaven-gold/10"
              >
                <Info className="mr-2 h-4 w-4" />
                {isGettingHelp ? "Getting strategic coaching..." : "Need Help? Get Suggestions"}
              </Button>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Enhanced Suggestions Overlay */}
      <SuggestionsOverlay 
        isOpen={showSuggestionsOverlay}
        onClose={() => setShowSuggestionsOverlay(false)}
        suggestions={helpSuggestions}
        onSelectSuggestion={handleSelectSuggestion}
      />

      <div className="text-center text-sm text-gray-500 mt-4">
        <p>⚠️ Remember: This isn't a game. It's training for a spiritual battlefield.</p>
      </div>

      {/* Conversion complete dialog */}
      <ConversionComplete 
        open={showConversionComplete} 
        onOpenChange={setShowConversionComplete} 
      />
    </div>
  );
};

export default ChatInterface;